<template>
  <div class="book" id="bookingForm">
    <div class="mb-2 hidden flex-col gap-5 lg:mb-10 lg:flex w-full">
      <a @click.prevent="goBack" href="#back" class="inline-flex items-center gap-2 text-[16px] font-semibold text-dark ">
        <span class="text-lg leading-none">‹</span>
        <span class="underline underline-offset-4">Go back</span>
      </a>

      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <span class="flex h-8 w-8 items-center justify-center rounded-full text-dark lg:bg-[#70D6AF] lg:text-[#0F5138] lg:h-16 lg:w-16">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 5.625V7.5H11.875V5.625C11.875 3.90625 10.4688 2.5 8.75 2.5C6.99219 2.5 5.625 3.90625 5.625 5.625ZM3.125 7.5V5.625C3.125 2.53906 5.625 0 8.75 0C11.8359 0 14.375 2.53906 14.375 5.625V7.5H15C16.3672 7.5 17.5 8.63281 17.5 10V17.5C17.5 18.9062 16.3672 20 15 20H2.5C1.09375 20 0 18.9062 0 17.5V10C0 8.63281 1.09375 7.5 2.5 7.5H3.125Z" fill="currentColor"/>
            </svg>
          </span>
          <h1 class="text-lg lg:text-4xl font-semibold">Checkout</h1>
        </div>
      </div>
    </div>
    <div v-if="visibleMessages.length" class="mb-6 space-y-3">
      <div
          v-for="(message, index) in visibleMessages"
          :key="`${message}-${index}`"
          class="rounded-2xl border border-dashed border-[#F5A623] bg-[#FFF8E8] px-4 py-3 text-[15px] text-dark"
      >
        {{ message }}
      </div>
    </div>
    <div class="min-h-screen relative z-10">
        <Form class="flex flex-wrap gap-8 pb-12 lg:pt-6 lg:flex-nowrap lg:pb-0"
              v-slot="{ errors, invalid, failed }"
              @submit="onSubmit"
              @invalid-submit="onInvalidSubmit">
          <div class="w-full lg:max-w-[832px] flex-grow md:pr-4 lg:pb-20 relative">
            <Loader v-if="bookingProcessing">
              <h3 class="text-center text-xl">Processing booking...</h3>
            </Loader>
            <div>

              <div class="mb-6 lg:mt-0 -mx-4 px-4 lg:mx-0 lg:px-0 pt-2 lg:pt-0 bg-light lg:bg-white">
                <div class="flex items-center justify-between gap-4 lg:hidden">
                  <div class="flex items-center gap-4">
                    <span class="flex h-8 w-8 items-center justify-center rounded-full text-dark bg-[#70D6AF] text-[#0F5138] lg:h-16 lg:w-16">
                      <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.625 5.625V7.5H11.875V5.625C11.875 3.90625 10.4688 2.5 8.75 2.5C6.99219 2.5 5.625 3.90625 5.625 5.625ZM3.125 7.5V5.625C3.125 2.53906 5.625 0 8.75 0C11.8359 0 14.375 2.53906 14.375 5.625V7.5H15C16.3672 7.5 17.5 8.63281 17.5 10V17.5C17.5 18.9062 16.3672 20 15 20H2.5C1.09375 20 0 18.9062 0 17.5V10C0 8.63281 1.09375 7.5 2.5 7.5H3.125Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <h1 class="text-lg lg:text-4xl font-semibold">Checkout</h1>
                  </div>
                  <div class="text-right">
                    <div class="text-xl font-medium">{{ formattedCheckoutOrderTotal }}</div>
                    <div class="mt-1 text-sm text-dark/60">AUD (inc. GST)</div>
                  </div>
                </div>
                <voucher
                    v-model="vouchers"
                    @details="voucherDetails"
                ></voucher>
              </div>


              <PaymentStripeExpress
                  v-if="options.gateway.express"
                  :config="options.gateway"
                  :pre-checkout-validator="() => validateBeforeExpressCheckout()"
                  ref="paymentExpress"
                  @errors="addPaymentErrors"
                  @setPage="setPage"
                  @top="top"
                  @paymentConfirmed="expressPaymentConfirmed"
              ></PaymentStripeExpress>

              <div class="mb-4">
                <div class="grid md:grid-cols-2 gap-4">
                  <Field
                      v-slot="{ field, errors }"
                      v-model="customer.firstName"
                      label="First name"
                      name="firstName"
                      rules="required" slim>
                    <div class="customer-field mb-4">
                      <ErrorWrapper :submitted="submitted" :errors="errors">
                        <FieldLabel for="firstName" :required="true">First Name</FieldLabel>
                        <div class="flex-grow">
                          <input
                              v-bind="field"
                              type="text"
                              class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                              :class="{ 'border border-alert-error': errors.length }"
                          />
                          <FieldError :errors="errors" />
                        </div>
                      </ErrorWrapper>
                    </div>
                  </Field>
                  <Field
                      v-slot="{ field, errors }"
                      v-model="customer.lastName"
                      label="Last name"
                      name="lastName"
                      rules="required" slim>
                    <div class="customer-field mb-4">
                      <ErrorWrapper :submitted="submitted" :errors="errors">
                        <FieldLabel for="lastName" :required="true">Last Name</FieldLabel>
                        <div class="flex-grow">
                          <input
                              v-bind="field"
                              type="text"
                              class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                              :class="{ 'border-alert-error': errors.length }"
                          />
                          <FieldError :errors="errors" />
                        </div>
                      </ErrorWrapper>
                    </div>
                  </Field>
                </div>
                <Field
                    v-slot="{ field, errors }"
                    v-model="customer.email"
                    label="Email"
                    name="email"
                    rules="required|email">
                  <div class="customer-field mb-4">
                    <ErrorWrapper :submitted="submitted" :errors="errors">
                      <FieldLabel for="email" :required="true">Email</FieldLabel>
                      <div class="flex-grow">
                        <input
                          v-bind="field"
                          type="email"
                          class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                          :class="{ 'border-alert-error': errors.length }"
                        />
                        <FieldError :errors="errors" />
                      </div>
                    </ErrorWrapper>
                  </div>
                </Field>
                <Field
                    v-slot="{ field, errors }"
                    :value="customer.phone"
                    label="Phone"
                    name="phone"
                    rules="required|international_phone"
                >
                  <div class="customer-field mb-2 w-full lg:w-2/3">
                    <ErrorWrapper :submitted="submitted" :errors="errors">
                      <FieldLabel for="phone" :required="true">Phone</FieldLabel>
                      <div class="flex-grow">
                        <IntlTelInput
                            :value="field.value"
                            :model-value="field.value"
                            @update:modelValue="field.onChange"
                            :input-props="{
                              onBlur: field.onBlur,
                              type: 'text',
                              name: field.name,
                              class: 'w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white ' + (errors.length ? 'border-alert-error' : ''),
                            }"
                            @changeNumber="(num) => { field.onChange(num); updatePhoneNumber(num); }"
                            :options="{
                              initialCountry: 'au',
                            }" />
                        <FieldError :errors="errors" />
                      </div>
                    </ErrorWrapper>
                  </div>
                </Field>
              </div>


              <div v-if="rcaCoupons" class="pb-4 px-6 relative">
                <Field
                  v-slot="{ field, errors }"
                  label="RCA membership number"
                  v-model="rcaNumber"
                  rules="required"
                  name="rcaNumber"
                >
                  <div class="customer-field mb-2">
                    <FieldLabel for="rcaNumber" :required="true">RCA membership number</FieldLabel>
                    <div class="flex-grow">
                      <input
                        id="rcaNumber"
                        v-bind="field"
                        type="text"
                        class="w-full leading-tight p-4 rounded-sm border bg-light shadow-inner border-light focus:border-transparent focus:ring-2 focus:ring-primary focus:ring-inset focus:bg-white"
                        :class="{ 'border-alert-error': errors.length }"
                      />
                      <FieldError :errors="errors" />
                    </div>
                  </div>
                </Field>
              </div>

              <div v-if="options.gateway && options.gateway.type && needsPayment" v-cloak id="paymentForm">
                <PaymentCard
                    v-if="options.gateway.type === 'card'"
                    :config="options.gateway"
                    ref="payment" />
                <PaymentStripeLegacy
                    v-if="['stripe:rezdy', 'stripe:via'].includes(options.gateway.type)"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors" />
                <PaymentStripe3d
                    v-if="options.gateway.type === 'stripe:3d'"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors"/>
                <PaymentStripe
                    v-if="options.gateway.type === 'stripe'"
                    :config="options.gateway"
                    ref="payment"
                    @errors="addPaymentErrors"
                    @setPage="setPage"
                    @top="top" />
              </div>
            </div>
          </div>
          <div class="relative w-full pb-6 lg:max-w-[386px]">
            <BookingSummary
              class="-mx-4 rounded-none px-6 py-10 lg:sticky lg:top-[100px] lg:mx-0 lg:rounded-[32px] lg:px-6 lg:py-6"
              edit-url="/cart/"
              from="checkout"
              :items="items"
              :quote-enabled="true"
              :show-edit-links="true"
              total-label="Order Total"
              :vouchers="voucherInfo"
              :warnings="checkoutSummaryWarnings"
              @loading="setQuoteLoading"
              @errors="addQuoteErrors"
            >
              <form-errors
                :errors="errorList"
                :submitted="submitted"
              ></form-errors>


              <questions
                :submitted="submitted"
                v-model="answered"
                @errors="addQuestionErrors"
              ></questions>

              <confirmations
                v-if="items.length > 0"
                :submitted="submitted"
                v-model="confirmed"
              ></confirmations>


              <div class="py-4">
                <button
                  type="submit"
                  :disabled="toClose || childrenNeedAdultOrSenior || loading || submitQuoteErrors.length > 0 || quoteLoading || !readyToPay"
                  class="hidden w-full items-center justify-center gap-2 rounded-full bg-primary p-2 py-4 text-center font-semibold leading-none text-dark disabled:opacity-70 lg:flex"
                  @click="submitted = true"
                >
                  <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z" fill="currentColor"/>
                  </svg>
                  <span v-if="loading && submitted">Processing...</span>
                  <span v-if="!loading">Complete Your Booking</span>
                </button>

                <terms class="mb-2"></terms>
                <div class="mt-2 text-sm">
                  <p>
                    This site is protected by reCAPTCHA and the Google
                    <a
                      href="https://policies.google.com/privacy"
                      target="google_terms"
                      class="text-dark underline"
                    >Privacy Policy</a>
                    and
                    <a
                      href="https://policies.google.com/terms"
                      target="google_terms"
                      class="text-dark underline"
                    >Terms of Service</a>
                    apply.
                  </p>
                </div>
              </div>
            </BookingSummary>
          </div>

          <div class="fixed inset-x-0 bottom-0 z-[9000] bg-white px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 lg:hidden">
            <button
              type="submit"
              :disabled="toClose || childrenNeedAdultOrSenior || loading || submitQuoteErrors.length > 0 || quoteLoading || !readyToPay"
              class="flex min-h-[54px] w-full items-center justify-center gap-3 rounded-full bg-primary px-6 text-center text-[18px] font-semibold leading-none text-dark disabled:opacity-70"
              @click="submitted = true"
            >
              <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.4688 2.5 8.75 2.5C6.99219 2.5 5.625 3.90625 5.625 5.625ZM3.125 7.5V5.625C3.125 2.53906 5.625 0 8.75 0C11.8359 0 14.375 2.53906 14.375 5.625V7.5H15C16.3672 7.5 17.5 8.63281 17.5 10V17.5C17.5 18.9062 16.3672 20 15 20H2.5C1.09375 20 0 18.9062 0 17.5V10C0 8.63281 1.09375 7.5 2.5 7.5H3.125Z" fill="currentColor"/>
              </svg>
              <span v-if="loading && submitted">Processing...</span>
              <span v-else>Buy Tickets</span>
            </button>
          </div>
        </Form>
    </div>
  </div>
</template>
<script>
import {mapActions, mapState, mapWritableState} from "pinia";
import {Field, Form} from 'vee-validate';
import api from "./../api";
import useStore from "./../store"
import { bookingItemsTooCloseMessages, trackBookingComplete } from "../helpers"
import {formatCurrency} from "../helpers/format"

//components
import BookingSummary from "../components/BookingSummary.vue";
import Voucher from "./../components/Voucher";
import Terms from "./../components/Terms";
import FieldLabel from "./../components/FieldLabel";
import FieldError from "../components/FieldError.vue";
import Confirmations from "../components/Confirmations.vue";
import PaymentCard from "./../components/PaymentCard.vue";
import PaymentStripeLegacy from "../components/PaymentStripeLegacy.vue";
import PaymentStripe from "../components/PaymentStripe.vue";
import PaymentStripe3d from "../components/PaymentStripe3d.vue";
import FormErrors from "../components/FormErrors.vue";
import ErrorWrapper from "../components/ErrorWrapper.vue";
import Loader from "../components/Loader.vue";
import PaymentStripeExpress from "../components/PaymentStripeExpress.vue";
import useReadyToPay from "../helpers/useReadyToPay";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";
import Questions from "../components/Questions.vue";
import { childTicketNeedsAdultOrSenior, childTicketValidationMessage } from "../helpers/cart";

const childTicketCheckoutMessage = `${childTicketValidationMessage} Please return to your cart to add an adult or senior ticket, or remove the child ticket.`;

export default {
  name: 'Checkout',
  components: {
    BookingSummary,
    Questions,
    IntlTelInput,
    PaymentStripeExpress,
    ErrorWrapper,
    PaymentStripe3d,
    PaymentStripe,
    PaymentCard,
    PaymentStripeLegacy,
    Confirmations,
    FieldError,
    Voucher,
    Terms,
    Form,
    Field,
    FieldLabel,
    FormErrors,
    Loader
  },
  props: {
    visibleMessages: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const { readyToPay } = useReadyToPay();

    return {
      readyToPay
    }
  },
  data() {
    return {
      voucherInfo: false,
      submitErrors: [],
      submitQuoteErrors: [],
      paymentErrors: [],
      submitted: false,
      loading: false,
      quoteLoading: false,
      confirmed: false,
      answered: true,
      bookingProcessing: false,
    };
  },
  computed: {
    errorList(){
      return [...this.submitErrors, ...this.paymentErrors, ...this.submitQuoteErrors];
    },
    rcaCoupons() {
      if (this.vouchers.length < 1) {
        return false;
      }

      if (this.options.racCoupons < 1) {
        return false;
      }

      return (
        this.vouchers.filter((v) => {
          return this.options.racCoupons.filter((rc) => v.includes(rc)).length > 0;
        }).length > 0
      );
    },
    ...mapWritableState(useStore, [
      "options",
      "items",
      "attractions",
      "customer",
      "creditCard",
      "voucher",
      "rcaNumber",
      "vouchers",
      "quote",
      "g-recaptcha-response",
      "status",
      "form",
      "totalPeople"
    ]),
    ...mapState(useStore, [
      "cartSubtotal",
    ]),
    checkoutOrderTotal() {
      if (this.quote && Object.prototype.hasOwnProperty.call(this.quote, 'totalDue')) {
        return Number(this.quote.totalDue ?? 0);
      }

      return Number(this.cartSubtotal ?? 0);
    },
    formattedCheckoutOrderTotal() {
      return formatCurrency(this.checkoutOrderTotal);
    },
    childrenNeedAdultOrSenior() {
      return this.items.some((item) => {
        const attraction = this.attractions.find((current) => Number(current.id) === Number(item.attraction));
        return childTicketNeedsAdultOrSenior(item, attraction);
      });
    },
    checkoutSummaryWarnings() {
      return [
        ...this.bookingTimeConflictWarnings,
        ...(this.childrenNeedAdultOrSenior ? [childTicketCheckoutMessage] : []),
      ];
    },
    bookingTimeConflictWarnings() {
      return bookingItemsTooCloseMessages(this.items);
    },
    toClose() {
      return this.bookingTimeConflictWarnings.length > 0;
    },
    needsPayment() {
      return !(this.quote.totalDue === 0 && this.vouchers.length > 0);
    },
  },
  beforeMount() {
    (async () => {
      await this.loadAttractions();

      if (window?.fathom) {
        window.fathom.trackEvent('booking-form-opened');
      }
    })();
  },
  methods: {
    ...mapActions(useStore, {
      loadAttractions: "loadAttractions",
      setStatus: "setStatus",
      setBooking: "setBooking",
      setBookingToken: "setBookingToken",
    }),
    updatePhoneNumber(number){
      this.customer.phone = number;
    },
    setQuoteLoading(loading) {
      this.quoteLoading = loading;
    },
    voucherDetails(data) {
      this.voucherInfo = data;
      this.paymentErrors = [];
    },
    async top() {
      this.$emit('top')
    },
    async setPage(page) {
      this.$emit('setPage', page)
    },
    async recaptcha() {
      // Try twice - network glitches on mobile are often momentary
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await this.$recaptchaLoaded();
          this["g-recaptcha-response"] = await this.$recaptcha("login");
          return; // Success
        } catch (error) {
          console.warn(`reCAPTCHA attempt ${attempt}/2 failed:`, error);
          if (attempt < 2) {
            // Brief pause before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            // Final attempt failed - continue without token
            // Server-side can handle missing reCAPTCHA
            this["g-recaptcha-response"] = "";
          }
        }
      }
    },
    async onSubmit() {
      this.submitted = true;
      const validForBooking = await this.validateBeforeBooking();
      if (!validForBooking) {
        return;
      }
      this.submitErrors = [];
      this.paymentErrors = [];
      this.loading = true;

      if (!this.needsPayment){
        await this.recaptcha();
      }

      if (this.needsPayment && ['stripe', 'stripe:3d'].includes(this.options.gateway.type)){
        const validToken = await this.$refs.payment.generate();
        if (!validToken){
          this.loading = false;
          return;
        }
      }

      await this.book();
    },
    async expressPaymentConfirmed() {
      this.submitted = true;
      const validForBooking = await this.validateBeforeBooking();
      if (!validForBooking) {
        return;
      }
      this.submitErrors = [];
      this.paymentErrors = [];
      this.loading = true;

      if (!this.needsPayment){
        await this.recaptcha();
      }

      await this.book('express');
    },
    async validateBeforeExpressCheckout() {
      this.submitted = true;
      this.submitErrors = [];
      this.paymentErrors = [];

      return await this.validateBeforeBooking();
    },
    async validateBeforeBooking() {
      const questionsError = 'Please answer the required questions';
      this.submitErrors = this.submitErrors.filter((error) => (
        error !== questionsError && error !== childTicketCheckoutMessage
      ));
      if (!this.answered){
        this.submitErrors.push(questionsError);
        await this.$nextTick();
        const questionInput = document.querySelector('#bookingForm [id^=\"answer_\"]');

        if (questionInput) {
          questionInput.focus();
          questionInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      if (this.childrenNeedAdultOrSenior) {
        this.submitErrors.push(childTicketCheckoutMessage);
        await this.$nextTick();
        const summary = document.querySelector('#bookingForm aside');

        if (summary) {
          summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        return false;
      }

      if (!this.confirmed){
        await this.$nextTick();
        const confirmationInput = document.querySelector('#bookingForm [id^=\"confirmation_\"]');

        if (confirmationInput) {
          confirmationInput.focus();
          confirmationInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      if (this.toClose){
        return false;
      }

      return true;
    },
    focusFirstError(errors) {
      const errorKeys = Object.keys(errors);

      const fieldsToMoveToEnd = ['firstName', 'lastName', 'email', 'phone'];
      const fieldsSet = new Set(fieldsToMoveToEnd);

      const reorderedErrors = [
        ...errorKeys.filter(field => !fieldsSet.has(field)),
        ...errorKeys.filter(field => fieldsSet.has(field))
      ];

      const firstErrorKey = reorderedErrors[0];
      const errorInput = document.querySelector(`#bookingForm [name="${firstErrorKey}"]`);

      if (errorInput) {
        errorInput.focus();
        errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async book(mode = 'payment') {
      this.bookingProcessing = true;

      this.setStatus(null);
      let savedForm = this.form;
      savedForm.mode = mode

      return api
          .order(savedForm)
          .then(async (response) => {
            if (response.data.handle && this.options.gateway.type === 'stripe') {
              if (mode === 'express'){
                await this.$refs.paymentExpress.handleServerResponse(response.data);
              } else {
                await this.$refs.payment.handleServerResponse(response.data);
              }
            } else {
              this.setBooking(response.data?.booking);
              this.setBookingToken(
                response.data?.retailBooking?.token ??
                response.data?.booking?.token ??
                response.data?.token ??
                false
              );
              this.setStatus("success");
              trackBookingComplete(response.data?.booking)
              this.$emit('setPage', 'thanks');
              this.$emit('top')
            }

          })
          .catch((error) => {
            console.error(error);
            console.log("THE ERROR", error.response);

            if (error?.response?.data?.requestStatus?.error) {
              this.submitErrors.push(error?.response.data?.requestStatus?.error?.errorMessage);
            }

            if (error?.response?.data?.errors?.errorMessage) {
              this.submitErrors.push(error.response.data.errors.errorMessage);
            }

            if (error?.response?.data?.errors && error.response.data?.errors?.errorCode !== "1") {
              for (const key in error.response.data.errors) {
                if(key !== "errorCode"){
                  if (Array.isArray(error.response.data.errors[key])){
                    this.submitErrors = [...this.submitErrors, ...error.response.data.errors[key]];
                  } else {
                    this.submitErrors = [...this.submitErrors, error.response.data.errors[key]];
                  }
                }
              }
            }

            this.setStatus("failed");
          })
          .then(() => {
            this.loading = false;
            this.bookingProcessing = false;
          });
    },

    addQuoteErrors(errors) {
      this.submitQuoteErrors = errors;
    },
    addQuestionErrors(errors) {
      this.questionErrors = errors;
    },
    addPaymentErrors(errors) {
      this.paymentErrors = errors;

      if (errors.length <= 0) {
        return;
      }

      const errorInput = document.querySelector(`#paymentForm`);

      if (errorInput) {
        errorInput.focus();
        errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onInvalidSubmit({ values, errors, results }) {
      this.focusFirstError(errors);
    },
    goBack() {
      window.history.back()
    }
  },
};
</script>
